import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import matchService from '../services/matchService';
import teamService from '../services/teamService';
import tournamentService from '../services/tournamentService';
import '../styles/MatchesList.css';
import { useTranslation } from 'react-i18next';

const MatchesList = () => {
	const [matches, setMatches] = useState([]);
	const [filteredMatches, setFilteredMatches] = useState([]);
	const [teams, setTeams] = useState([]);
	const [tournaments, setTournaments] = useState([]); // Add state for tournaments
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [homeTeamFilter, setHomeTeamFilter] = useState('');
	const [awayTeamFilter, setAwayTeamFilter] = useState('');
	const [sportTypeFilter, setSportTypeFilter] = useState('');
	const [dateFilter, setDateFilter] = useState('');
	const [tournamentFilter, setTournamentFilter] = useState(''); // Add state for tournament filter

	const { t } = useTranslation();

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [teamData, matchData, tournamentData] = await Promise.all([
					teamService.getAllTeams(),
					matchService.getAllMatches(),
					tournamentService.getAllTournaments(),
				]);

				const sortedMatches = matchData.sort((a, b) => new Date(a.date) - new Date(b.date));
				setTeams(teamData);
				setMatches(sortedMatches);
				setTournaments(tournamentData);
				setFilteredMatches(sortedMatches);
				setLoading(false);
			} catch (err) {
				console.error('Error fetching data:', err);
				setError(`Failed to fetch data: ${err.message}`);
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	useEffect(() => {
		let updatedMatches = [...matches];

		// Filter by Home Team
		if (homeTeamFilter) {
			updatedMatches = updatedMatches.filter(
				(match) => match.homeTeam.id === homeTeamFilter
			);
		}

		// Filter by Away Team
		if (awayTeamFilter) {
			updatedMatches = updatedMatches.filter(
				(match) => match.awayTeam.id === awayTeamFilter
			);
		}

		// Filter by Sport Type
		if (sportTypeFilter) {
			updatedMatches = updatedMatches.filter(
				(match) =>
					match.homeTeam.sportType === sportTypeFilter ||
					match.awayTeam.sportType === sportTypeFilter
			);
		}

		// Filter by Tournament
		if (tournamentFilter) {
			updatedMatches = updatedMatches.filter(
				(match) => match.tournament?.id === tournamentFilter
			);
		}

		// Filter by Date (Past or Future)
		if (dateFilter) {
			const now = new Date();
			if (dateFilter === 'past') {
				updatedMatches = updatedMatches.filter(
					(match) => new Date(match.date) < now
				);
			} else if (dateFilter === 'future') {
				updatedMatches = updatedMatches.filter(
					(match) => new Date(match.date) >= now
				);
			}
		}

		updatedMatches.sort((a, b) => new Date(a.date) - new Date(b.date));
		setFilteredMatches(updatedMatches);
	}, [homeTeamFilter, awayTeamFilter, sportTypeFilter, dateFilter, tournamentFilter, matches]);

	const formatDate = (date) => {
		const d = new Date(date);
		const day = String(d.getDate()).padStart(2, '0');
		const month = String(d.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
		const year = d.getFullYear();
		const hours = String(d.getHours()).padStart(2, '0');
		const minutes = String(d.getMinutes()).padStart(2, '0');
		return `${day}.${month}.${year} ${hours}:${minutes}`;
	};

	if (loading) return <div>Loading...</div>;
	if (error) return <div style={{ color: 'red' }}>{error}</div>;

	return (
		<div>
			<h1>All Matches</h1>

			{/* Filters */}
			<div>
				<label>Home Team: </label>
				<select
					value={homeTeamFilter}
					onChange={(e) => setHomeTeamFilter(Number(e.target.value))}
				>
					<option value="">All Home Teams</option>
					{teams.map((team) => (
						<option key={team.id} value={team.id}>
							{team.name}
						</option>
					))}
				</select>
			</div>
			<div>
				<label>Away Team: </label>
				<select
					value={awayTeamFilter}
					onChange={(e) => setAwayTeamFilter(Number(e.target.value))}
				>
					<option value="">All Away Teams</option>
					{teams.map((team) => (
						<option key={team.id} value={team.id}>
							{team.name}
						</option>
					))}
				</select>
			</div>
			<div>
				<label>Sport Type: </label>
				<select
					value={sportTypeFilter}
					onChange={(e) => setSportTypeFilter(e.target.value)}
				>
					<option value="">All</option>
					<option value="FOOTBALL">Football</option>
					<option value="HOCKEY">Hockey</option>
				</select>
			</div>
			<div>
				<label>Date: </label>
				<select
					value={dateFilter}
					onChange={(e) => setDateFilter(e.target.value)}
				>
					<option value="">All</option>
					<option value="past">Past Matches</option>
					<option value="future">Upcoming Matches</option>
				</select>
			</div>
			<div>
				<label>Tournament: </label>
				<select
					value={tournamentFilter}
					onChange={(e) => setTournamentFilter(Number(e.target.value))}
				>
					<option value="">All Tournaments</option>
					{tournaments.map((tournament) => (
						<option key={tournament.id} value={tournament.id}>
							{t(`tournaments.${tournament.name}`, { year: tournament.year })}
						</option>
					))}
				</select>
			</div>

			{/* Matches Table */}
			{filteredMatches.length > 0 ? (
				<table>
					<thead>
						<tr>
							<th>ID</th>
							<th>Home Team</th>
							<th>Away Team</th>
							<th>Sport Type</th>
							<th>Date</th>
							<th>Tournament</th>
							<th>Home Team Points</th>
							<th>Away Team Points</th>
							<th>Draw Points</th>
							<th>Home Score</th>
							<th>Away Score</th>
						</tr>
					</thead>
					<tbody>
						{filteredMatches.map((match) => (
							<tr key={match.id}>
								<td>
									<Link to={`/match-details/${match.id}`}>{match.id}</Link>
								</td>
								<td>{match.homeTeam.name}</td>
								<td>{match.awayTeam.name}</td>
								<td>
									{match.homeTeam.sportType === match.awayTeam.sportType
										? match.homeTeam.sportType
										: `${match.homeTeam.sportType}/${match.awayTeam.sportType}`}
								</td>
								<td>{formatDate(match.date)}</td>
								<td>{match.tournament?.name
                                                    ? t(`tournaments.${match.tournament.name}`, { year: match.tournament.year })
                                                    : t('noTournament')}</td>
								<td>{match.homeTeamPoints}</td>
								<td>{match.awayTeamPoints}</td>
								<td>{match.drawPoints}</td>
								<td>{match.scoreHome}</td>
								<td>{match.scoreAway}</td>
							</tr>
						))}
					</tbody>
				</table>
			) : (
				<div>No matches match the selected filters.</div>
			)}
		</div>
	);
};

export default MatchesList;
